/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — notes-checklist.ts
   runDisclosureChecklist(), addMissingDisclosures(), checklist rendering
   ═══════════════════════════════════════════════════════════════════════ */

import { state } from './state';
import { meta, esc, rows, sum, computeNetIncome, el } from './utils';
import { buildComparativeSection } from './statements';
import { type DataContext, type CheckItem, type Detection, buildPPESchedule, buildDebtSchedule, getDefaultDisclosureText } from './notes-rules';
import { generateNotes } from './notes-questionnaire';
import { DEBT_PATTERN, INTANGIBLE_PATTERN, LEASE_PATTERN, PPE_PATTERN, INVENTORY_PATTERN } from './patterns';

export function addMissingDisclosures(): void {
  const notesEl = el('notes-statement');
  if (!notesEl) return;

  const m = meta();
  // Build rich schedule tables for context
  const dataContext: DataContext = {
    ppeTable: buildPPESchedule(),
    debtTable: buildDebtSchedule(),
    equityTable: buildComparativeSection('equity'),
  };

  let notesHTML: string = notesEl.innerHTML.toLowerCase();
  const hasNotes: boolean = notesHTML.length > 50;
  if (!hasNotes) {
    // Generate notes first if none exist
    generateNotes();
    notesHTML = notesEl.innerHTML.toLowerCase();
  }

  // Re-run detections to find what's still missing
  function notesMention(keyword: string): boolean { return notesHTML.indexOf(keyword.toLowerCase()) >= 0; }
  function hasItem(section: string, pattern: RegExp): boolean {
    return rows(state.currentData, section).some(function(r) { return pattern.test(r.label); });
  }
  function hasAnyItems(section: string): boolean { return rows(state.currentData, section).length > 0; }

  const checks: CheckItem[] = [
    { id: 'basis', detected: true, required: true, disclosed: notesMention('basis of accounting') },
    { id: 'estimates', detected: true, required: true, disclosed: notesMention('use of estimates') },
    { id: 'cash', detected: hasItem('current-assets', /cash/i), required: false, disclosed: notesMention('cash equivalent') || notesMention('cash and cash') },
    { id: 'revenue', detected: hasAnyItems('revenue'), required: false, disclosed: notesMention('revenue recognition') || notesMention('revenue is recognized') },
    { id: 'receivables', detected: hasItem('current-assets', /receivable/i), required: false, disclosed: notesMention('accounts receivable') || notesMention('allowance') },
    { id: 'inventory', detected: hasItem('current-assets', INVENTORY_PATTERN), required: false, disclosed: notesMention('inventory') },
    { id: 'ppe', detected: hasItem('noncurrent-assets', PPE_PATTERN), required: false, disclosed: notesMention('property and equipment') || notesMention('depreciation') },
    { id: 'intangibles', detected: hasItem('noncurrent-assets', INTANGIBLE_PATTERN), required: false, disclosed: notesMention('intangible') || notesMention('goodwill') || notesMention('amortiz') },
    { id: 'debt', detected: hasItem('current-liab', DEBT_PATTERN) || hasItem('noncurrent-liab', DEBT_PATTERN), required: false, disclosed: notesMention('debt') || notesMention('notes payable') || notesMention('loan') || notesMention('line of credit') },
    { id: 'leases', detected: hasItem('current-liab', LEASE_PATTERN) || hasItem('noncurrent-liab', LEASE_PATTERN) || hasItem('noncurrent-assets', LEASE_PATTERN), required: false, disclosed: notesMention('lease') },
    { id: 'taxes', detected: true, required: true, disclosed: notesMention('income tax') || notesMention('tax') },
    { id: 'equity', detected: hasAnyItems('equity'), required: false, disclosed: notesMention('equity') || notesMention('stockholder') || notesMention('member') },
    { id: 'related', detected: true, required: true, disclosed: notesMention('related party') },
    { id: 'concentrations', detected: true, required: true, disclosed: notesMention('concentration') || notesMention('credit risk') || notesMention('fdic') },
    { id: 'contingencies', detected: true, required: true, disclosed: notesMention('commitment') || notesMention('contingenc') || notesMention('litigation') },
    { id: 'subsequent', detected: true, required: true, disclosed: notesMention('subsequent event') },
  ];

  // Check going concern
  const totalCA: number = sum(state.currentData, 'current-assets');
  const totalCL: number = sum(state.currentData, 'current-liab');
  const ni: number = computeNetIncome(state.currentData);
  if (Math.abs(totalCL) > totalCA || ni < 0) {
    checks.push({ id: 'goingconcern', detected: true, required: true, disclosed: notesMention('going concern') });
  }

  // Find missing items
  const missingIds: string[] = [];
  checks.forEach(function(c) {
    if ((c.detected || c.required) && !c.disclosed) {
      missingIds.push(c.id);
    }
  });

  if (missingIds.length === 0) {
    alert('All disclosures are complete — no missing items to add.');
    return;
  }

  // Append missing disclosures to notes
  const existingHTML: string = notesEl.innerHTML;
  // Remove closing </div> of notes-output if present, so we append inside
  const closingDiv: number = existingHTML.lastIndexOf('</div>');
  const beforeClose: string = closingDiv >= 0 ? existingHTML.substring(0, closingDiv) : existingHTML;
  const afterClose: string = closingDiv >= 0 ? existingHTML.substring(closingDiv) : '';

  let addedHTML: string = '<div class="auto-disclosures" style="margin-top:24px;border-top:2px solid #3b82f6;padding-top:20px">';
  addedHTML += '<p style="font-size:0.85rem;font-weight:600;color:#3b82f6;margin-bottom:16px">The following disclosures were auto-generated to address gaps identified by the Disclosure Checklist. Review and customize as needed.</p>';

  let noteNum: number = (existingHTML.match(/Note\s+\d+/gi) || []).length;
  missingIds.forEach(function(id) {
    const text: string = getDefaultDisclosureText(id, m, dataContext);
    if (text) {
      noteNum++;
      // Replace "Note —" with numbered note
      const numberedText: string = text.replace(/<h3>Note —/, '<h3>Note ' + noteNum + ' —');
      addedHTML += numberedText;
    }
  });
  addedHTML += '</div>';

  notesEl.innerHTML = beforeClose + addedHTML + afterClose;

  // Show notes output if hidden
  const outputWrapper = el('notes-output-wrapper');
  if (outputWrapper) outputWrapper.style.display = 'block';
  const questionnaire = el('notes-questionnaire');
  if (questionnaire) questionnaire.style.display = 'none';

  // Re-run checklist to show updated status
  runDisclosureChecklist();

  // Scroll to top of checklist
  el('checklist-output')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function runDisclosureChecklist(): void {
  const m = meta();
  const notesEl = el('notes-statement');
  const notesHTML: string = notesEl ? notesEl.innerHTML.toLowerCase() : '';
  const hasNotes: boolean = notesHTML.length > 50;

  // Helper: check if any line item label in a section matches a pattern
  function hasItem(section: string, pattern: RegExp): boolean {
    return rows(state.currentData, section).some(function(r) { return pattern.test(r.label); });
  }
  function hasAnyItems(section: string): boolean { return rows(state.currentData, section).length > 0; }

  // Helper: check if notes mention a keyword
  function notesMention(keyword: string): boolean {
    if (!hasNotes) return false;
    return notesHTML.indexOf(keyword.toLowerCase()) >= 0;
  }

  // ── Detect what's on the statements ──
  const detections: Detection[] = [
    { id: 'basis', label: 'Basis of Accounting', detected: true, required: true,
      reason: 'Required for all financial statements',
      disclosed: notesMention('basis of accounting'),
      asc: 'ASC 235-10-50',
      guidance: 'Requires disclosure of the basis of accounting used (accrual, cash, tax, etc.). If a special-purpose framework is used, the nature of the framework must be described.',
      url: 'https://asc.fasb.org/235' },
    { id: 'estimates', label: 'Use of Estimates', detected: true, required: true,
      reason: 'Required for all GAAP financial statements',
      disclosed: notesMention('use of estimates'),
      asc: 'ASC 275-10-50',
      guidance: 'Requires disclosure that the preparation of financial statements in conformity with GAAP requires management to make estimates and assumptions that affect reported amounts. Must state that actual results could differ.',
      url: 'https://asc.fasb.org/275' },
    { id: 'cash', label: 'Cash and Cash Equivalents', detected: hasItem('current-assets', /cash/i), required: false,
      reason: 'Cash detected in current assets',
      disclosed: notesMention('cash equivalent') || notesMention('cash and cash'),
      asc: 'ASC 230-10-50',
      guidance: 'Requires disclosure of the policy for determining which items are treated as cash equivalents (generally instruments with original maturities of three months or less). Must disclose any restrictions on cash balances.',
      url: 'https://asc.fasb.org/230' },
    { id: 'revenue', label: 'Revenue Recognition (ASC 606)', detected: hasAnyItems('revenue'), required: false,
      reason: 'Revenue items detected',
      disclosed: notesMention('revenue recognition') || notesMention('revenue is recognized'),
      asc: 'ASC 606-10-50',
      guidance: 'Requires disclosure of the nature, amount, timing, and uncertainty of revenue and cash flows from contracts with customers. Must describe performance obligations, significant judgments, and contract balances.',
      url: 'https://asc.fasb.org/606' },
    { id: 'receivables', label: 'Accounts Receivable / Allowance', detected: hasItem('current-assets', /receivable/i), required: false,
      reason: 'Receivables detected in current assets',
      disclosed: notesMention('accounts receivable') || notesMention('allowance'),
      asc: 'ASC 310-10-50 / ASC 326-20',
      guidance: 'Requires disclosure of the methodology used to estimate the allowance for credit losses (CECL model under ASC 326). Must describe major categories of receivables, significant concentrations, and credit quality information.',
      url: 'https://asc.fasb.org/326' },
    { id: 'inventory', label: 'Inventory', detected: hasItem('current-assets', INVENTORY_PATTERN), required: false,
      reason: 'Inventory detected in current assets',
      disclosed: notesMention('inventory'),
      asc: 'ASC 330-10-50',
      guidance: 'Requires disclosure of the basis of stating inventories (FIFO, weighted average, etc.) and whether stated at cost or lower of cost and net realizable value. Must disclose significant write-downs or reversals.',
      url: 'https://asc.fasb.org/330' },
    { id: 'ppe', label: 'Property and Equipment', detected: hasItem('noncurrent-assets', PPE_PATTERN), required: false,
      reason: 'Fixed assets detected in non-current assets',
      disclosed: notesMention('property and equipment') || notesMention('depreciation'),
      asc: 'ASC 360-10-50',
      guidance: 'Requires disclosure of depreciation method, useful lives or rates, balances of major classes of depreciable assets, accumulated depreciation, and depreciation expense for the period. Must disclose impairments if applicable.',
      url: 'https://asc.fasb.org/360' },
    { id: 'intangibles', label: 'Intangible Assets / Goodwill', detected: hasItem('noncurrent-assets', INTANGIBLE_PATTERN), required: false,
      reason: 'Intangible assets detected in non-current assets',
      disclosed: notesMention('intangible') || notesMention('goodwill') || notesMention('amortiz'),
      asc: 'ASC 350-20-50 / ASC 350-30-50',
      guidance: 'Goodwill: disclose carrying amount, impairment testing approach, and any impairment losses (ASC 350-20). Finite-lived intangibles: disclose amortization method, useful lives, gross carrying amounts, accumulated amortization, and estimated future amortization expense (ASC 350-30).',
      url: 'https://asc.fasb.org/350' },
    { id: 'debt', label: 'Debt / Notes Payable', detected: hasItem('current-liab', DEBT_PATTERN) || hasItem('noncurrent-liab', DEBT_PATTERN), required: false,
      reason: 'Debt instruments detected in liabilities',
      disclosed: notesMention('debt') || notesMention('notes payable') || notesMention('loan') || notesMention('line of credit'),
      asc: 'ASC 470-10-50',
      guidance: 'Requires disclosure of terms, interest rates, maturity dates, collateral, and covenants for all significant debt instruments. Must present aggregate maturities for the five years following the balance sheet date. Disclose any defaults or covenant violations.',
      url: 'https://asc.fasb.org/470' },
    { id: 'leases', label: 'Leases (ASC 842)', detected: hasItem('current-liab', LEASE_PATTERN) || hasItem('noncurrent-liab', LEASE_PATTERN) || hasItem('noncurrent-assets', LEASE_PATTERN), required: false,
      reason: 'Lease-related items detected on balance sheet',
      disclosed: notesMention('lease'),
      asc: 'ASC 842-20-50 / ASC 842-30-50',
      guidance: 'Lessees must disclose ROU assets and lease liabilities, lease cost components, weighted-average remaining lease term, discount rate, and a maturity analysis of lease liabilities. Must distinguish operating from finance leases. Practical expedient elections must be disclosed.',
      url: 'https://asc.fasb.org/842' },
    { id: 'taxes', label: 'Income Taxes', detected: true, required: true,
      reason: 'Required — entity tax status must be disclosed',
      disclosed: notesMention('income tax') || notesMention('tax'),
      asc: 'ASC 740-10-50',
      guidance: 'Requires disclosure of the nature of income tax expense, deferred tax assets/liabilities, uncertain tax positions (UTPs), and open tax years. Pass-through entities must disclose their tax status and that income taxes are the responsibility of individual owners/members.',
      url: 'https://asc.fasb.org/740' },
    { id: 'equity', label: 'Equity / Stockholders\' Equity', detected: hasAnyItems('equity'), required: false,
      reason: 'Equity items on balance sheet',
      disclosed: notesMention('equity') || notesMention('stockholder') || notesMention('member'),
      asc: 'ASC 505-10-50',
      guidance: 'Requires disclosure of authorized, issued, and outstanding shares for each class of stock, par or stated value, dividend rates, and any preferences or restrictions. Must disclose changes in equity accounts during the period.',
      url: 'https://asc.fasb.org/505' },
    { id: 'related', label: 'Related Party Transactions', detected: true, required: true,
      reason: 'Required disclosure — must confirm presence or absence',
      disclosed: notesMention('related party'),
      asc: 'ASC 850-10-50',
      guidance: 'Requires disclosure of the nature of the relationship, a description of transactions, dollar amounts, and any amounts due to/from related parties. Must disclose even if transactions are at arm\'s length terms. Common relationships: owners, officers, family members, affiliated entities.',
      url: 'https://asc.fasb.org/850' },
    { id: 'concentrations', label: 'Concentrations of Credit Risk', detected: true, required: true,
      reason: 'Required for entities with financial instruments',
      disclosed: notesMention('concentration') || notesMention('credit risk') || notesMention('fdic'),
      asc: 'ASC 825-10-50 / ASC 275-10-50',
      guidance: 'Requires disclosure of all significant concentrations of credit risk from financial instruments (including cash deposits exceeding FDIC limits). Must disclose concentrations in revenue sources, customer base, suppliers, geographic areas, or labor markets that make the entity vulnerable to near-term severe impact.',
      url: 'https://asc.fasb.org/825' },
    { id: 'contingencies', label: 'Commitments and Contingencies', detected: true, required: true,
      reason: 'Required — must confirm presence or absence',
      disclosed: notesMention('commitment') || notesMention('contingenc') || notesMention('litigation'),
      asc: 'ASC 450-20-50 / ASC 440-10-50',
      guidance: 'Loss contingencies (ASC 450): must disclose the nature of the contingency and either an accrual or an estimate of the possible loss (or range). Commitments (ASC 440): must disclose material purchase commitments, guarantees, and other contractual obligations not recognized on the balance sheet.',
      url: 'https://asc.fasb.org/450' },
    { id: 'subsequent', label: 'Subsequent Events', detected: true, required: true,
      reason: 'Required — evaluation date must be disclosed',
      disclosed: notesMention('subsequent event'),
      asc: 'ASC 855-10-50',
      guidance: 'Requires disclosure of the date through which subsequent events have been evaluated. Recognized events (Type I): adjust the financial statements. Non-recognized events (Type II): disclose the nature and financial impact. Must distinguish between recognized and non-recognized events.',
      url: 'https://asc.fasb.org/855' },
    { id: 'goingconcern', label: 'Going Concern', detected: false, required: false,
      reason: 'Required only if substantial doubt exists',
      disclosed: notesMention('going concern'),
      asc: 'ASC 205-40',
      guidance: 'Management must evaluate whether there is substantial doubt about the entity\'s ability to continue as a going concern for one year from the financial statement issuance date. If substantial doubt exists, must disclose the conditions, management\'s plans, and whether those plans alleviate the doubt.',
      url: 'https://asc.fasb.org/205' },
  ];

  // Check going concern indicators
  const totalCA: number = sum(state.currentData, 'current-assets');
  const totalCL: number = sum(state.currentData, 'current-liab');
  const netIncome: number = computeNetIncome(state.currentData);
  if (totalCL > totalCA || netIncome < 0) {
    const gc = detections.find(function(d) { return d.id === 'goingconcern'; });
    if (gc) {
      gc.detected = true;
      gc.required = true;
      gc.reason = (totalCL > totalCA ? 'Negative working capital detected. ' : '') + (netIncome < 0 ? 'Net loss detected. ' : '') + 'Evaluate whether going concern disclosure is needed.';
    }
  }

  // ── Render checklist ──
  let html: string = '<div class="stmt-header">' +
    '<div class="company">' + esc(m.company) + '</div>' +
    '<div class="title">U.S. GAAP Disclosure Checklist</div>' +
    '<div class="period">' + esc(m.period) + '</div>' +
  '</div>';

  if (!hasNotes) {
    html += '<p style="color:var(--danger);font-weight:600;margin:16px 0">Notes have not been generated yet. Generate notes first, then run this checklist to compare against GAAP requirements.</p>';
  }

  let disclosed: number = 0, missing: number = 0, na: number = 0, warnings: number = 0;

  html += '<table class="stmt-table" style="width:100%"><colgroup><col style="width:30%"><col style="width:15%"><col style="width:55%"></colgroup><tbody>';
  html += '<tr style="font-size:0.78rem;font-weight:600;color:#64748b;border-bottom:2px solid #e1e5eb"><td>GAAP Disclosure Requirement</td><td style="text-align:center">Status</td><td>Details</td></tr>';

  detections.forEach(function(d) {
    let status: string = '';
    let statusClass: string = '';
    let detail: string = '';

    if (!d.detected && !d.required) {
      status = '— N/A';
      statusClass = 'color:#94a3b8';
      detail = 'Not applicable — item not detected on financial statements.';
      na++;
    } else if (d.disclosed) {
      status = '&#10003; Disclosed';
      statusClass = 'color:#16a34a;font-weight:600';
      detail = d.reason;
      disclosed++;
    } else if (d.detected || d.required) {
      if (hasNotes) {
        status = '&#10007; Missing';
        statusClass = 'color:#dc2626;font-weight:600';
        detail = d.reason + ' — disclosure not found in generated notes. Update the Notes questionnaire to address this item.';
        missing++;
      } else {
        status = '&#9888; Pending';
        statusClass = 'color:#d97706;font-weight:600';
        detail = d.reason + ' — generate notes to satisfy this requirement.';
        warnings++;
      }
    }

    html += '<tr class="checklist-item" style="border-bottom:1px solid #f1f5f9">';
    html += '<td style="padding:10px 8px;font-weight:500">';
    html += '<span class="checklist-label">' + d.label + '</span>';
    if (d.asc) {
      html += '<a href="' + d.url + '" target="_blank" rel="noopener" class="asc-ref" title="' + esc(d.guidance) + '">' + esc(d.asc) + '</a>';
    }
    html += '</td>';
    html += '<td style="padding:10px 8px;text-align:center;' + statusClass + '">' + status + '</td>';
    html += '<td style="padding:10px 8px;font-size:0.83rem;color:#475569">' + detail + '</td>';
    html += '</tr>';
  });

  html += '</tbody></table>';

  // Summary bar
  const total: number = detections.filter(function(d) { return d.detected || d.required; }).length;
  html += '<div style="margin-top:24px;padding:16px 20px;background:#f8fafc;border-radius:8px;border:1px solid #e2e8f0;display:flex;gap:24px;flex-wrap:wrap">';
  html += '<div style="font-weight:600;font-size:0.95rem">Summary</div>';
  html += '<div style="color:#16a34a"><strong>' + disclosed + '</strong> Disclosed</div>';
  if (missing > 0) html += '<div style="color:#dc2626"><strong>' + missing + '</strong> Missing</div>';
  if (warnings > 0) html += '<div style="color:#d97706"><strong>' + warnings + '</strong> Pending</div>';
  html += '<div style="color:#94a3b8"><strong>' + na + '</strong> N/A</div>';
  if (total > 0) {
    const pct: number = Math.round((disclosed / total) * 100);
    html += '<div style="margin-left:auto;font-weight:600;font-size:1.1rem;color:' + (pct === 100 ? '#16a34a' : pct >= 70 ? '#d97706' : '#dc2626') + '">' + pct + '% Complete</div>';
  }
  html += '</div>';

  // Add "Fix Missing" button when there are gaps
  if (missing > 0) {
    html += '<div style="margin-top:16px;padding:16px 20px;background:#fef2f2;border:1px solid #fecaca;border-radius:8px">';
    html += '<div style="display:flex;align-items:center;gap:16px;flex-wrap:wrap">';
    html += '<div style="flex:1;min-width:200px">';
    html += '<div style="font-weight:600;color:#991b1b;margin-bottom:4px">' + missing + ' Missing Disclosure' + (missing !== 1 ? 's' : '') + '</div>';
    html += '<div style="font-size:0.83rem;color:#7f1d1d">Auto-generate standard GAAP disclosure language for all missing items and append to your notes. You can review and customize the text afterward.</div>';
    html += '</div>';
    html += '<button class="btn btn-primary" data-action="addMissingDisclosures" style="white-space:nowrap">Add Missing Disclosures to Notes</button>';
    html += '</div></div>';
  }

  const checklistOut = el('checklist-output');
  if (checklistOut) checklistOut.innerHTML = html;
}
